import * as Yup from 'yup'
import clsx from 'clsx'
import {useFormik} from 'formik'
import {useMutation} from '@apollo/client'
import {Link, useLocation, useNavigate} from 'react-router-dom'
import {useState} from 'react'
import {toast} from 'react-toastify'
import {toAbsoluteUrl} from '../../../../_metronic/helpers'
import {VERSION_APP} from '../../helpers/consts'
import {PASSWORD_MIN_LENGTH, PASSWORD_REQUIREMENTS} from '../../helpers/passwordPolicy'
import {FORCE_CHANGE_PASSWORD, RESET_PASSWORD} from '../../gql/mutations/authMutations'

export const ResetPasswordPage = () => {
  const defaultLogo = toAbsoluteUrl('/media/tfi/logo_policytools.png')
  const location = useLocation()
  const navigate = useNavigate()
  const [resetPassword, {loading}] = useMutation(RESET_PASSWORD)
  const [forceChangePassword, {loading: forceLoading}] = useMutation(FORCE_CHANGE_PASSWORD)
  const [resetDone, setResetDone] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)

  const params = new URLSearchParams(location.search)
  const forced = params.get('forced') === '1' || params.get('forced') === 'true'
  const email = params.get('email') || ''
  const uid = params.get('uid') || ''
  const token = params.get('token') || ''
  const isInvitation = params.get('invite') === '1' || params.get('invite') === 'true'
  const hasToken = Boolean(uid && token)
  const canSubmit = forced ? Boolean(email) : hasToken

  const formik = useFormik({
    initialValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema: Yup.object().shape({
      currentPassword: forced
        ? Yup.string()
            .min(3, 'Minimum 3 symbols')
            .max(50, 'Maximum 50 symbols')
            .required('Current password is required')
        : Yup.string().notRequired(),
      password: Yup.string()
        .min(PASSWORD_MIN_LENGTH, `Password must be at least ${PASSWORD_MIN_LENGTH} characters`)
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .matches(/[a-z]/, 'Password must contain at least one lowercase letter')
        .matches(/[0-9]/, 'Password must contain at least one number')
        .matches(/[^A-Za-z0-9]/, 'Password must contain at least one special character')
        .test(
          'not-same-as-current',
          'New password must be different from your current password',
          function (value) {
            if (!forced) return true
            const currentPassword = this.parent.currentPassword
            if (!value || !currentPassword) return true
            return value !== currentPassword
          }
        )
        .max(50, 'Maximum 50 symbols')
        .required('Password is required'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Password confirmation is required'),
    }),
    onSubmit: async (values) => {
      if (!canSubmit) {
        toast.error(forced ? 'Missing email for forced password change.' : 'Invalid or expired reset link.')
        return
      }

      try {
        const response = forced
          ? await forceChangePassword({
              variables: {
                email,
                currentPassword: values.currentPassword,
                newPassword: values.password,
              },
            })
          : await resetPassword({
              variables: {
                uid,
                token,
                newPassword: values.password,
              },
            })

        const payload = forced ? response.data?.forceChangePassword : response.data?.resetPassword
        const ok = payload?.ok
        const message = payload?.message || ''
        if (ok) {
          toast.success(message || 'Password updated.')
          setResetDone(true)
          setTimeout(() => navigate('/login'), 1200)
        } else {
          toast.error(message || (forced ? 'Password change failed.' : 'Invalid or expired reset link.'))
        }
      } catch (error) {
        toast.error('An error occurred. Please try again.')
      }
    },
  })

  return (
    <div className='d-flex flex-column flex-lg-row flex-column-fluid h-100'>
      <div className='d-flex flex-column flex-lg-row-fluid w-lg-50 p-10 order-2 order-lg-1'>
        <div className='d-flex flex-center flex-column flex-lg-row-fluid'>
          <div className='w-lg-500px p-10'>
            <form className='form w-100' onSubmit={formik.handleSubmit} noValidate>
              <div className='text-center mb-11'>
                <img src='/media/tfi/logoNColorP.png' width={200} alt='logo' />
                <h1 className='text-dark fw-bolder mb-3'>
                  {forced ? 'Change Password' : isInvitation ? 'Set Up Your Account' : 'Reset Password'}
                </h1>
                <div className='text-gray-500 fw-semibold fs-6'>
                  {forced
                    ? 'Enter your current password and set a new one to continue.'
                    : isInvitation
                    ? 'Create a password to activate your account.'
                    : 'Choose a new password for your account.'}
                </div>
              </div>

              {!canSubmit && (
                <div className='mb-10 alert alert-danger'>
                  {forced ? 'Missing email for forced password change.' : 'Invalid or expired reset link.'}
                </div>
              )}
              {resetDone && (
                <div className='mb-10 bg-light-info p-8 rounded'>
                  <div className='text-info'>Password updated. Redirecting to login.</div>
                </div>
              )}

              <div
                className='mb-8 alert py-4'
                style={{
                  backgroundColor: '#EEF4FF',
                  color: '#0F172A',
                  border: '1px solid #93C5FD',
                }}
              >
                <div className='fw-bold mb-2'>Password requirements:</div>
                <ul className='mb-0 ps-6'>
                  {PASSWORD_REQUIREMENTS.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                  <li>Must be different from your current password</li>
                </ul>
              </div>

              {forced && (
                <>
                  <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder text-dark'>Email</label>
                    <input className='form-control bg-transparent' type='email' value={email} disabled />
                  </div>
                  <div className='fv-row mb-8'>
                    <label className='form-label fs-6 fw-bolder text-dark'>Current password</label>
                    <div className='position-relative'>
                      <input
                        placeholder='Current password'
                        {...formik.getFieldProps('currentPassword')}
                        className={clsx(
                          'form-control bg-transparent',
                          {'is-invalid': formik.touched.currentPassword && formik.errors.currentPassword},
                          {
                            'is-valid': formik.touched.currentPassword && !formik.errors.currentPassword,
                          }
                        )}
                        type={showCurrentPassword ? 'text' : 'password'}
                        name='currentPassword'
                      />
                      <button
                        type='button'
                        onClick={() => setShowCurrentPassword((p) => !p)}
                        style={{
                          color: '#111',
                          border: 0,
                          backgroundColor: 'transparent',
                          margin: 0,
                          padding: 0,
                          position: 'absolute',
                          right:
                            formik.touched.currentPassword && formik.errors.currentPassword
                              ? '36px'
                              : formik.touched.currentPassword
                                ? '36px'
                                : '10px',
                          bottom:
                            formik.touched.currentPassword && formik.errors.currentPassword
                              ? '35px'
                              : '10px',
                        }}
                      >
                        {showCurrentPassword ? (
                          <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                        ) : (
                          <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                        )}
                      </button>
                    </div>
                    {formik.touched.currentPassword && formik.errors.currentPassword && (
                      <div className='fv-plugins-message-container'>
                        <span role='alert' className='text-danger'>
                          {formik.errors.currentPassword}
                        </span>
                      </div>
                    )}
                  </div>
                </>
              )}

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>New password</label>
                <div className='position-relative'>
                  <input
                    placeholder='New password'
                    {...formik.getFieldProps('password')}
                    className={clsx(
                      'form-control bg-transparent',
                      {'is-invalid': formik.touched.password && formik.errors.password},
                      {
                        'is-valid': formik.touched.password && !formik.errors.password,
                      }
                    )}
                    type={showNewPassword ? 'text' : 'password'}
                    name='password'
                  />
                  <button
                    type='button'
                    onClick={() => setShowNewPassword((p) => !p)}
                    style={{
                      color: '#111',
                      border: 0,
                      backgroundColor: 'transparent',
                      margin: 0,
                      padding: 0,
                      position: 'absolute',
                      right:
                        formik.touched.password && formik.errors.password
                          ? '36px'
                          : formik.touched.password
                            ? '36px'
                            : '10px',
                      bottom:
                        formik.touched.password && formik.errors.password ? '35px' : '10px',
                    }}
                  >
                    {showNewPassword ? (
                      <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                    ) : (
                      <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                    )}
                  </button>
                </div>
                {formik.touched.password && formik.errors.password && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.password}
                    </span>
                  </div>
                )}
              </div>

              <div className='fv-row mb-8'>
                <label className='form-label fs-6 fw-bolder text-dark'>Confirm password</label>
                <div className='position-relative'>
                  <input
                    placeholder='Confirm password'
                    {...formik.getFieldProps('confirmPassword')}
                    className={clsx(
                      'form-control bg-transparent',
                      {
                        'is-invalid':
                          formik.touched.confirmPassword && formik.errors.confirmPassword,
                      },
                      {
                        'is-valid':
                          formik.touched.confirmPassword && !formik.errors.confirmPassword,
                      }
                    )}
                    type={showConfirmPassword ? 'text' : 'password'}
                    name='confirmPassword'
                  />
                  <button
                    type='button'
                    onClick={() => setShowConfirmPassword((p) => !p)}
                    style={{
                      color: '#111',
                      border: 0,
                      backgroundColor: 'transparent',
                      margin: 0,
                      padding: 0,
                      position: 'absolute',
                      right:
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? '36px'
                          : formik.touched.confirmPassword
                            ? '36px'
                            : '10px',
                      bottom:
                        formik.touched.confirmPassword && formik.errors.confirmPassword
                          ? '35px'
                          : '10px',
                    }}
                  >
                    {showConfirmPassword ? (
                      <i className='bi bi-eye-slash fs-4' style={{color: 'inherit'}} />
                    ) : (
                      <i className='bi bi-eye fs-4' style={{color: 'inherit'}} />
                    )}
                  </button>
                </div>
                {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                  <div className='fv-plugins-message-container'>
                    <span role='alert' className='text-danger'>
                      {formik.errors.confirmPassword}
                    </span>
                  </div>
                )}
              </div>

              <div className='d-grid mb-10'>
                <button
                  type='submit'
                  className='btn btn-primary mt-4'
                  disabled={!canSubmit || formik.isSubmitting || !formik.isValid || loading || forceLoading}
                >
                  {!loading && !forceLoading && (
                    <span className='indicator-label'>
                      {forced ? 'Change password' : isInvitation ? 'Set password' : 'Update password'}
                    </span>
                  )}
                  {(loading || forceLoading) && (
                    <span className='indicator-progress' style={{display: 'block'}}>
                      Please wait...
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>

              <div className='text-gray-500 text-center fw-semibold fs-6'>
                <Link to='/login' className='link-primary'>
                  Back to login
                </Link>
              </div>
            </form>
          </div>
        </div>

        <div className='d-flex flex-center flex-wrap px-5'>
          <div>
            <span>{VERSION_APP}</span>
          </div>
        </div>
      </div>

      <div
        className='d-flex flex-lg-row-fluid w-lg-50 bgi-size-cover bgi-position-center order-1 order-lg-2'
        style={{backgroundImage: `url(${toAbsoluteUrl('/media/misc/auth-bg.png')})`}}
      >
        <div className='d-flex flex-column flex-center py-15 px-5 px-md-15 w-100'>
          <span className='mb-12'>
            <img alt='Logo' src={defaultLogo} className='h-75px' />
          </span>
          <img
            className='mx-auto w-275px w-md-60 w-xl-500px mb-10 mb-lg-20'
            src={toAbsoluteUrl('/media/tfi/login.png')}
            alt=''
          />
          <h1 className='text-white fs-2qx fw-bolder text-center mb-7'>
            Fast, Efficient and Productive
          </h1>
        </div>
      </div>
    </div>
  )
}
